import { useEffect, useState, RefObject } from 'react'

interface UseElementVisibilityOptions {
  threshold?: number // How much of the element needs to be visible (0-1)
}

const useElementVisibility = (
  ref: RefObject<HTMLElement>,
  options: UseElementVisibilityOptions = {}
) => {
  const { threshold = 0 } = options
  const [isVisible, setIsVisible] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const checkVisibility = () => {
      if (!ref.current) {return}

      const rect = ref.current.getBoundingClientRect()
      const windowHeight = window.innerHeight

      const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0)
      const elementHeight = rect.height
      const visibilityRatio = Math.max(0, visibleHeight / elementHeight)

      const elementIsVisible =
        visibilityRatio >= threshold && rect.bottom > 0 && rect.top < windowHeight

      setIsVisible(elementIsVisible)
      setIsInitialized(true)
    }

    window.addEventListener('scroll', checkVisibility, { passive: true })
    checkVisibility() // Check initial state

    return () => window.removeEventListener('scroll', checkVisibility)
  }, [ref, threshold])

  return { isVisible, isInitialized }
}

export default useElementVisibility
